import type { DataPoint } from "@suse-o11y/data-point";
import { isBefore, isWithinInterval } from "@suse-o11y/date-time";
import { type Time, isTime } from "@suse-o11y/date-time/time";
import { find } from "@suse-o11y/find";
import { findLast } from "@suse-o11y/find-last";
import { first } from "@suse-o11y/first";
import { isPresent } from "@suse-o11y/is-present";
import { last } from "@suse-o11y/last";
import { piped } from "@suse-o11y/piped";
import { prop } from "@suse-o11y/prop";
import { sortedIndexWith } from "@suse-o11y/sorted-index-with";

export const ALL_SINGLE_POINT_CALCULATIONS = ["first", "first-number", "last", "last-number"] as const;

export type SinglePointCalculation = (typeof ALL_SINGLE_POINT_CALCULATIONS)[number];

export function isSinglePointCalculation(value: string | Time): value is Time | SinglePointCalculation {
	return isTime(value) || ALL_SINGLE_POINT_CALCULATIONS.includes(value as SinglePointCalculation);
}

export function calculateSinglePoint(
	calculation: Time | SinglePointCalculation,
	points: ReadonlyArray<DataPoint>,
): undefined | DataPoint {
	switch (calculation) {
		case "first": {
			return first(points);
		}

		case "first-number": {
			return find(points, piped(prop(1), isPresent));
		}

		case "last": {
			return last(points);
		}

		case "last-number": {
			return findLast(points, piped(prop(1), isPresent));
		}

		default: {
			/**
			 * Return undefined when the calculation time is out of the points time interval
			 */
			if (
				points.length === 0 ||
				!isWithinInterval(calculation, { start: points[0][0], end: points[points.length - 1][0] })
			) {
				return undefined;
			}

			const coordIndex = sortedIndexWith(points, ([time]) => isBefore(time, calculation));

			return points[coordIndex];
		}
	}
}
